import { useEffect, useRef, type ReactNode } from "react";
import { Icon } from "./Icon";
import "./sidepanel.css";

/**
 * A right-hand side panel, not a modal.
 *
 * DESIGN_SYSTEM.md §4: "Use a side panel (not a modal) for anything that
 * benefits from keeping dashboard context visible behind it (e.g., a drift-flag
 * drill-down per FR-DASH-04) — reserve true modals for short, blocking
 * confirmations."
 *
 * That distinction is functional here, not stylistic: a Comms Manager working
 * top-down through a severity-ranked triage list needs to see where they are in
 * that list while reading one item's evidence.
 */
export function SidePanel({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
}: {
  open: boolean;
  title: string;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    restoreFocusTo.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      // Returning focus to whatever opened the panel is what keeps keyboard
      // triage viable — otherwise every close drops the user at the page top.
      restoreFocusTo.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="sidepanel__scrim" onMouseDown={onClose}>
      <div
        className="sidepanel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panelRef}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="sidepanel__head">
          <div>
            <h2 className="sidepanel__title">{title}</h2>
            {subtitle ? (
              <div className="sidepanel__subtitle">{subtitle}</div>
            ) : null}
          </div>
          <button
            type="button"
            className="sidepanel__close"
            onClick={onClose}
            aria-label="Close"
          >
            <Icon name="close" />
          </button>
        </header>
        <div className="sidepanel__body">{children}</div>
        {footer ? <footer className="sidepanel__foot">{footer}</footer> : null}
      </div>
    </div>
  );
}
